import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Button, Empty, Flex, message, Modal, Result, Tooltip } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, Eye, Plus, RotateCw, Settings, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getListOrganizationRolesQueryKey,
  useDeleteOrganizationRole,
  useListOrganizationRoles,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import {
  CreateOrganizationRoleDrawer,
  EditOrganizationRoleDrawer,
} from '../../../../modules/organization/components/organization-role-drawer';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';

import type { ListOrganizationRolesResponseDtoItemsItem } from '../../../../api/generated/model';

const SettingsRolesRoute = () => {
  const queryClient = useQueryClient();

  const { selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canEditRoles = can('settings', 'manageMembershipsAndRoles');

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<ListOrganizationRolesResponseDtoItemsItem | null>(
    null,
  );

  const { data: rolesResponse, error, status, refetch } = useListOrganizationRoles(organizationId);
  const roles = rolesResponse?.data?.items;

  const deleteMutation = useDeleteOrganizationRole({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationRolesQueryKey(organizationId),
        });
        message.success('Deleted');
      },
      onError: () => {
        message.error('Failed to delete');
      },
    },
  });

  const content = {
    pending: <Loader />,
    error: isAxiosError(error) && error.response?.status === 403 ? (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You do not have permission to view or edit this page. Please contact your administrator."
        extra={
          <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
            Go back
          </Button>
        }
      />
    ) : (
      <Result
        status="error"
        title="Failed to load!"
        subTitle="Please try again. If this keeps happening, check your connection or contact support."
        extra={
          <Button icon={<RotateCw size="1em" />} onClick={() => refetch()}>
            Try again
          </Button>
        }
      />
    ),
    success: !roles?.length ? (
      <Empty description="No roles yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    ) : (
      <List
        items={roles}
        renderItem={(role) => {
          const isOwnRole = selectedMembership.role.id === role.id;
          const isOwnerRole = role.name.toLowerCase() === 'owner';
          const isViewOnly = isOwnRole || isOwnerRole;
          return {
            key: role.id,
            reactNode: (
              <Record
                left={<ShieldCheck size={20} />}
                title={role.name}
                description={role.description ?? undefined}
                right={
                  <Flex justify="flex-end">
                    <Tooltip title={isViewOnly ? 'View permissions' : 'Edit'}>
                      <Button
                        data-testid="role-open"
                        variant="text"
                        color="default"
                        type="text"
                        icon={isViewOnly ? <Eye size="1em" /> : <Settings size="1em" />}
                        onClick={() => setEditingRole(role)}
                      />
                    </Tooltip>
                    {canEditRoles && !isOwnRole && !isOwnerRole && (
                      <Tooltip title="Delete">
                        <Button
                          data-testid="role-delete"
                          variant="text"
                          color="danger"
                          icon={<Trash2 size="1em" />}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Delete role',
                              content: 'Are you sure you want to delete this role?',
                              okText: 'Delete',
                              cancelText: 'Cancel',
                              okButtonProps: {
                                danger: true,
                                loading: deleteMutation.isPending,
                                'data-testid': 'confirm',
                              },
                              onOk: () =>
                                deleteMutation.mutateAsync({
                                  organizationId,
                                  organizationRoleId: role.id,
                                }),
                            });
                          }}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                }
              />
            ),
          };
        }}
      />
    ),
  };

  return (
    <SettingsLayoutContent
      title="Roles & Permissions"
      description="Manage roles and access permissions for your workspace."
      action={
        canEditRoles && (
          <Button
            data-testid="role-create"
            color="default"
            variant="filled"
            size="large"
            icon={<Plus size="1em" />}
            onClick={() => setCreateDrawerOpen(true)}
          />
        )
      }
    >
      {content[status]}

      {createDrawerOpen && (
        <CreateOrganizationRoleDrawer
          open={createDrawerOpen}
          onClose={() => {
            setCreateDrawerOpen(false);
            queryClient.invalidateQueries({
              queryKey: getListOrganizationRolesQueryKey(organizationId),
            });
          }}
          organizationId={organizationId}
        />
      )}

      {editingRole && (
        <EditOrganizationRoleDrawer
          key={editingRole.id}
          open={!!editingRole}
          onClose={() => {
            setEditingRole(null);
            queryClient.invalidateQueries({
              queryKey: getListOrganizationRolesQueryKey(organizationId),
            });
          }}
          organizationId={organizationId}
          roleData={editingRole}
          canEdit={canEditRoles && editingRole.name.toLowerCase() !== 'owner'}
        />
      )}
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/roles')({
  component: SettingsRolesRoute,
});
